import { useRef, useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Backdrop, CircularProgress, FormControl, Box, Autocomplete, InputLabel, MenuItem, OutlinedInput, Tooltip, Select, Slider, TextField, Chip } from "@mui/material";
import { cloneDeep, isEmpty, isUndefined, isEqual, size } from "lodash";
import { AgGridReact } from "ag-grid-react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import { DPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import {SaveButton, AddButton, RemoveButton, SettingsButton} from "@plannel/components/ActionIconButton";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import ChartComposed from "../../../components/rechart/ChartComposed";

import demandService from "@plannel/services/data/demand-val-service";
import npiService from "@plannel/services/dp/dp-npi-history-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";

import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import dateUtil from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import ElasticSearchUtil from "@plannel/pages/utils/elastic-search-util";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import { useDispatch } from "react-redux";
import debug from "@plannel/utils/debug-util";

import { CartesianGrid, Legend, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip as RechartTooltip, XAxis, YAxis } from "recharts";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import HrchyLevelSegments from "@plannel/components/ui/HrchyLevelSegments";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";

const LifecycleMgmt = ({ t, viewName, title }) => {
  const reduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
  const reduxDispatch = useDispatch();
  const { buckets, dpSettingCode, dpConfig, module, customer, item } = codeUtil;
  const { ITEM_LEVEL, CUST_LEVEL } = dpSettingCode;
  const { BF_LEVEL_CONFIG } = dpConfig;
  const { DP } = module;
  const bucketCode = buckets;
  const { snackBarMessageColor: { saveSuccess, saveFail, inputError } } = colorUtil;
  const actions = ["phaseIn", "phaseOut"];
  const userCustomCd = "userCustom";
  const bfMeasure = codeUtil.dpMeasure.BF_QTY;

  /* linear, Exp, Log, Cubic Spline, Single Value */
  // 곡선 함수별 구분색 — 의미색이 아니라 카테고리 색이라 사용자 팔레트(colorSet)를 따른다.
  const functionColors = Array.from({ length: 7 }, (_, i) => colorUtil.colorSet(i));

  // 함수 칩. 색은 함수별로 유지한다(옆 차트 선 색과 짝이 맞아야 한다).
  const funcChipSx = colorUtil.tintChipSx;
  const functions = [
    { name: t("linear"), cd: "linear", color: functionColors[0] },
    { name: t("exp"), cd: "exp", color: functionColors[1] },
    { name: t("log"), cd: "log", color: functionColors[2] },
    { name: t("cubic"), cd: "cubic", color: functionColors[3] },
    { name: t("singleVal"), cd: "singleVal", color: functionColors[4] },
  ];

  const chartSeries = [
    { id: "baselineQty", field: t("baselineQty"), text: t("baselineQty"), typeOptions: { stork: functionColors[5], fill: functionColors[5] }, type: "line", groupName: t("qty") },
    { id: "linear", field: "linear", text: t("linear"), typeOptions: { stork: functionColors[0], fill: functionColors[0] }, type: "line", groupName: t("qty"), orientation: "right" },
    { id: "exp", field: "exp", text: t("exp"), typeOptions: { stork: functionColors[1], fill: functionColors[1] }, type: "line", groupName: t("qty") },
    { id: "log", field: "log", text: t("log"), typeOptions: { stork: functionColors[2], fill: functionColors[2] }, type: "line", groupName: t("qty") },
    { id: "cubic", field: "cubic", text: t("cubic"), typeOptions: { stork: functionColors[3], fill: functionColors[3] }, type: "line", groupName: t("qty") },
    { id: "singleVal", field: "singleVal", text: t("singleVal"), typeOptions: { stork: functionColors[4], fill: functionColors[4] }, type: "line", groupName: t("qty") },
    { id: "userCustom", field: "userCustom", text: t("userCustom"), typeOptions: { stork: functionColors[6], fill: functionColors[6] }, type: "line", groupName: t("qty") },
  ];
  /*filter*/
  const [inputValue, setInputValue] = useState('');
  const [inputCustValue, setInputCustValue] = useState('');

  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const [selectedItem, setSelectedItem] = useState(reduxViewState?.selectedItem ?? "");
  const [selectedCust, setSelectedCust] = useState(reduxViewState?.selectedCust ?? "");
  const [npiLifecycle, setNpiLifecycle] = useState(null);

  const [itemHrchyList, setItemHrchyList] = useState(reduxViewState?.itemHrchyList ?? []);
  const [custHrchyList, setCustHrchyList] = useState(reduxViewState?.custHrchyList ?? []);

  const [selectVersion, setSelectVersion] = useState(reduxViewState?.selectVersion ?? {});
  const [selectFunc, setSelectFunc] = useState(reduxViewState?.selectFunc ?? [functions[0], functions[1], functions[2], functions[3], functions[4]]);
  const [selectedFunc, setSelectedFunc] = useState(reduxViewState?.selectedFunc ?? "");
  const [selectAct, setSelectAct] = useState(reduxViewState?.selectAct ?? actions[0]);
  const [phaseStartDateIdx, setPhaseStartDateIdx] = useState(reduxViewState?.phaseStartDateIdx ?? 0);

  /*
    select item grid
   */
  const selectedItemsGridRef = useRef();
  const weekFormatHeader = useRef([]);
  const formatHeaders = useRef([]);
  const versionHeaders = useRef([]);
  const [gridHeader, setGridHeader] = useState([]);
  const [gridData, setGridData] = useState([]);
  const pivotData = useRef([]);
  const [demandResult, setDemandResult] = useState({});
  const [finishSave, setFinishSave] = useState(false);

  /*
    phase config chart
   */
  const [phaseStartDate, setPhaseStartDate] = useState(reduxViewState?.phaseStartDate ?? new ZDate());
  const [phaseRange, setPhaseRange] = useState(reduxViewState?.phaseRange ?? 1);
  const [phaseMaxRange, setPhaseMaxRange] = useState(reduxViewState?.phaseMaxRange ?? 1);
  const [phaseMinDate, setPhaseMinDate] = useState(phaseStartDate);
  const [phaseMaxDate, setPhaseMaxDate] = useState(phaseStartDate);

  const [sliderValue, setSliderValue] = useState(reduxViewState?.sliderValue ?? [10, 90]);
  const [versionDateList, setVersionDateList] = useState(reduxViewState?.versionDateList);

  const Spline = require("cubic-spline");

  const [xs, setXs] = useState([1]);
  const [ys, setYs] = useState({});

  const [chartData, setChartData] = useState([]);
  const [valueChartData, setValueChartData] = useState([]);

  /*
    phase calculated data
   */
  const calItemsGridRef = useRef();
  const [calItemsGridApi, setCalItemsGridApi] = useState(null);
  const [calGridHeader, setCalGridHeader] = useState([]);
  const [calGridData, setCalGridData] = useState([]);
  const [renderedGridData, setRenderedGridData] = useState([]);
  const loading = useRef(false);

  const [singleValue, setSingleValue] = useState(reduxViewState?.singleValue ?? Math.floor((sliderValue[1] + sliderValue[0]) / 2));
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
  const DIALOG_TITLE = { SAVE: "SAVE", CLOSE: "CLOSE" };

  /* level config modal*/
  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [levelConfig, setLevelConfig] = useState(reduxViewState?.levelConfig ?? {});
  const [itemLevelCds, setItemLevelCds] = useState(reduxViewState?.itemLevelCds ??[]);
  const [custLevelCds, setCustLevelCds] = useState(reduxViewState?.custLevelCds ??[]);
  const [modalCustLevelCd, setModalCustLevelCd] = useState({});
  const [modalItemLevelCd, setModalItemLevelCd] = useState({});

  const isCustomer = levelConfig?.objVal?.custLevelCd === customer.cd;
  const isItem = levelConfig?.objVal?.itemLevelCd === item.cd;

  const colorMap = {
    phaseBackground: "#E1FAEE",
    background: "#e0e0e0",
  };
  const unSelectedColor = "#dbdbdb";

  const strokeByField = (id) => {
    if (isEqual(id, selectedFunc)) {
      return 4;
    } else {
      return 1.5;
    }
  };

  function valuetext(value) {
    return `${value}%`;
  }

  const setCustomChart = (vChartData, rowData) => {
    if (rowData?.isNew) {
      for (const chart of vChartData) {
        chart.userCustom = rowData[chart.bucket];
      }
    }
    return vChartData;
  };

  const onSelectionChanged = () => {
    const selectedRows = calItemsGridRef.current.api.getSelectedRows();
    if (selectedRows[0]) {
      setSelectedFunc(selectedRows[0].func);
      if (selectedRows[0].isNew) {
        const newRowData = setCustomChart(valueChartData, selectedRows[0]);
        setValueChartData([...newRowData]);
      }
    } else {
      setSelectedFunc("");
    }
  };

  const handleSliderChange = (_event, newValue) => {
    setSliderValue(newValue);
    if (singleValue > newValue[1]) {
      setSingleValue(newValue[1]);
    }
    if (singleValue < newValue[0]) {
      setSingleValue(newValue[0]);
    }
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, sliderValue: newValue }));
  };

  const handleFuncChange = event => {
    setSelectFunc(event);
    loading.current = false;
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, selectFunc: event }));
  };
  
  const handleChange = event => {
    const { target: { value, name } } = event;
    if (name === t("actions")) {
      loading.current = false;
      setSelectAct(value);
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, selectAct: value }));
    } else if (name === CUST_LEVEL) {
      const custlevel = custLevelCds?.filter(function (el) {
        return el.id === value;
      })[0] ?? customer;
      setModalCustLevelCd(custlevel);
    } else if (name === ITEM_LEVEL) {
      const itemlevel = itemLevelCds.filter(function (el) { return el.id === value })[0] ?? item;
      setModalItemLevelCd(itemlevel)
    }
  };

  const statusValueCellRenderer = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  };

  const itemColumnDefs = [
    {
      headerName: isCustomer ? "customer": "customerHrchy",
      field: "customer",
      chartDataType: "category",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      editable: false,
      width: 150
    },
    {
      headerName: isItem ? "item" : "itemHrchy",
      field: "item",
      chartDataType: "category",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      editable: false,
      width: 150,
    },
    {
      headerName: "measure",
      field: "measure",
      pinned: "left", lockPinned: true,
      valueFormatter: statusValueCellRenderer,
      width: 180,
      chartDataType: "category",
      editable: false,
    },
    {
      headerName: "appliedFunc",
      field: "appliedFunc",
      pinned: "left", lockPinned: true,
      valueFormatter: parmas => t(parmas.value),
      width: 140,
      chartDataType: "category",
      editable: false,
    }
  ];

  const calculatedItemColumnDefs = [
    {
      headerName: isCustomer ? "customer": "customerHrchy",
      field: "customer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      width: 150,
      editable: false,
      checkboxSelection: true
    },
    {
      headerName: "customerId",
      field: "customerId",
      cellClass: "stringType",
      width: 150,
      hide: true,
      editable: false,
    },
    {
      headerName: isItem ? "item" : "itemHrchy",
      field: "item",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      editable: false,
      width: 150,
    },
    {
      headerName: "itemId",
      field: "itemId",
      hide: true,
      cellClass: "stringType",
      editable: false,
      width: 150,
    },
    {
      headerName: "function",
      field: "func",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      editable: false,
      valueFormatter: parmas => t(parmas.value),
      width: 150,
    },
  ];

  function getCellStyle(header, data, calculated) {
    const myIdx = formatHeaders.current.indexOf(header) + 1;
    const start = phaseStartDateIdx + 1;
    const end = phaseStartDateIdx + phaseRange;
    const phaseArea = myIdx >= start && myIdx <= end;
    if (data?.isNew) {
      return { backgroundColor: "rgb(255, 255, 210)" };
    } 
    return calculated && phaseArea ? { backgroundColor: colorMap.phaseBackground } : null;
  }

  const setColumnDefs = (headers, timeDimension, calculated = false) => {
    const formatHeaderList = [];
    for (const index in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, index)) {
        const current = headers[index];
        const temp = new ZDate(current);
        const date = new ZDate(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0);
        let format = dateUtil.headerDateFormatter(date, timeDimension);
        if (codeUtil.isWeekBucket(timeDimension)) {
          format = weekFormatHeader.current[current];
        }
        formatHeaderList.push(format);
      }
    }

    let columnDefs = [];
    if (!calculated) {
      columnDefs = itemColumnDefs;
    } else {
      columnDefs = calculatedItemColumnDefs;
    }

    for (const index in formatHeaderList) {
      if (Object.prototype.hasOwnProperty.call(formatHeaderList, index)) {
        const header = formatHeaderList[index];
        columnDefs.push({
          headerName: header,
          field: header,
          width: 110,
          singleClickEdit: false,
          type: ["numberColumn", "rightAligned"],
          chartDataType: "series",
          cellEditor: NumericEditor,
          editable: ({data}) => data.isNew,
          cellStyle({ data }) {
            return getCellStyle(header, data, calculated);
          },
        });
      }
    }

    return { colDef: columnDefs, gridHeaders: formatHeaderList };
  };

  const setGridDatas = (results, formatHeaderList) => {
    const datas = [];
    if (results) {
      for (const result of results) {
        const row = {
          appliedFunc: npiLifecycle?.funcCd ?? "none",
          itemCd: isItem ? result.itemCd : result.itemHrchyCd,
          item: isItem ? result.itemNm : result.itemHrchyNm,
          itemId: isItem ? result.itemId : result.itemHrchyId,
          customerCd: isCustomer ? result.customerCd : result.customerHrchyCd,
          customer: isCustomer ? result.customerNm : result.customerHrchyNm,
          customerId: isCustomer ? result.customerId : result.customerHrchyId,
          measure: bfMeasure.name,
          verNum: npiLifecycle?.verNum
        };

        for (let i = 0; i < formatHeaderList.length; i++) {
          const header = formatHeaderList[i];
          if (result.pivotData) {
            row[header] = result.pivotData[i];
          }
        }
        datas.push(row);
      }
    }
    formatHeaders.current = formatHeaderList;
    return datas;
  };

  const retrievePlanversion = () => {
    let header = [];
    let version = {};
    planVersionService.getDpScenarioMain()
      .then(response => {
        if (response) {
          const { results } = response.data;
          setSelectVersion(results);
          version = results;
          const { planConfigObj } = results;
          if (planConfigObj) {
            const { BUCKET_UNIT, PLANNING_RANGE, PLAN_START_DATE, START_DAY} = planConfigObj;
            header = dateUtil.makeTimeBucket(new ZDate(PLAN_START_DATE), dateUtil.calculateRangeDate(PLAN_START_DATE, BUCKET_UNIT, START_DAY, PLANNING_RANGE, true), BUCKET_UNIT, START_DAY);
            setXs(header);
            setVersionDateList(header);
            setPhaseMaxRange(header.length);
            setPhaseRange(header.length);
          }
        }
      }).catch(e => {
        debug(e);
      }).finally(() => {
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, versionDateList: header, selectVersion: version }));
      });
  };

  const calculatedPhaseState = () => {
    if (selectFunc.length < 1 || isEmpty(gridData)) {
      return gridData;
    }
    const data = gridData[0];
    const commonRow = {
      itemId: data.itemId,
      itemCd: data.itemCd,
      item: data.item,
      customerId: data.customerId,
      customerCd: data.customerCd,
      customer: data.customer,
    };

    const headerList = formatHeaders.current;
    const valueChart = [];
    const limit = pivotData?.current?.length;
    const datas = [];
    const chartHeader = versionHeaders.current;

    for (const func of selectFunc) {
      const funcYs = ys[func.name];
      if (isUndefined(funcYs)) {
        continue;
      }

      const row = {
        func: func.cd,
        ...commonRow,
      };
      for (let i = 0; i < phaseStartDateIdx; i++) {
        const header = headerList[i];
        const value = i < limit ? pivotData?.current[i] : 0;
        row[header] = value;
        const valueData = valueChart[i];
        valueChart[i] = {
          ...valueData,
          bucket: weekFormatHeader.current[chartHeader[i]],
          [func.cd]: value,
        };
      }

      for (let i = phaseStartDateIdx; i < chartHeader.length; i++) {
        const header = headerList[i];
        const value = i < limit ? pivotData.current[i] : 0;
        const percentage = !isUndefined(funcYs[i]) && funcYs[i] > 0 ? funcYs[i] / 100 : 0;
        const cal = Math.floor(value * percentage);
        row[header] = cal;
        const valueData = valueChart[i];
        valueChart[i] = {
          ...valueData,
          bucket: weekFormatHeader.current[chartHeader[i]],
          [func.cd]: cal,
        };
      }
      datas.push(row);
    }

    for (let i = 0; i < chartHeader.length; i++) {
      const value = i < limit ? pivotData.current[i] : 0;
      const valueData = valueChart[i];
      valueChart[i] = {
        ...valueData,
        bucket: weekFormatHeader.current[chartHeader[i]],
        [t(bfMeasure.name)]: value,
      };
    }
    if (!isEqual(selectedFunc, userCustomCd)) {
      setValueChartData(valueChart);
    }
    return datas;
  };

  const setCalGridHeaders = (header, dimension) => {
    setCalGridHeader([]);
    const calGridHeaders = setColumnDefs(header, dimension, true);
    setCalGridHeader(calGridHeaders.colDef);
  };

  const retrieveDemandByItem = () => {
    const startDate = new ZDate(selectVersion.startDate);
    if (isUndefined(startDate)) {
      return;
    }

    const endDate = dateUtil.calculateRangeDate(startDate, selectVersion.bucket, selectVersion.startDay, selectVersion.planningRange, true);
    const params = {
      selectVersionId: selectVersion.id,
      item: itemSearch.current,
      itemHrchyCd: selectedItem?.hrchyCd,
      customer: customerSearch.current,
      custHrchyCd: selectedCust?.hrchyCd,
      itemLevelCd: levelConfig?.objVal?.itemLevelCd,
      custLevelCd: levelConfig?.objVal?.custLevelCd,
      startDate,
      endDate,
      measureCd: bfMeasure.cd,
    };
    demandService.demandByItem(params)
    .then(response => {
      const result = response?.data?.resultData;
        if (result) {
          if (size(result?.pivotdata) > 0) {
            pivotData.current = result?.pivotdata[0].pivotData;
            setDemandResult(result?.pivotdata[0]);
          }
          const header = result.headers;
          versionHeaders.current = header;
          weekFormatHeader.current = result.formatHeaders;
          const gridHeaders = setColumnDefs(header, result.timeDimension);
          const newGridData = setGridDatas(result.results, gridHeaders.gridHeaders);
          setGridHeader(gridHeaders.colDef);
          setGridData(newGridData);
          setCalGridHeaders(header, result.timeDimension);
        }
      })
      .catch(e => {
        debug(e);
      });

    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...params }));
    }
  };

  const retrieveLifecycle = () => {
    npiService.getLifecycle({ item: isItem ? itemSearch.current : null,
      customer: isCustomer ? customerSearch.current : null,
      itemHrchyId: isItem ? null : selectedItem?.id,
      custHrchyId: isCustomer ? null : selectedCust?.id,
      custLevelCd: levelConfig?.objVal?.custLevelCd,
      itemLevelCd: levelConfig?.objVal?.itemLevelCd })
      .then(response => {
        if (response.data) {
          setNpiLifecycle(response.data);
          if (!reduxViewState?.selectedFunc) {
            setSelectedFunc(response.data.funcCd);
          }
          if (!reduxViewState?.phaseStartDate) {
            setPhaseStartDate(response.data.startOfLifeDt);
          }
          if (response.data.funcCd === functions[4].cd) {
            if (!reduxViewState?.singleValue) {
              setSingleValue(response.data.startValRate);
            }
          } else {
            if (reduxViewState?.sliderValue) {
              setSliderValue([response.data.startValRate, response.data.endValRate]);
            }
          }
        } else {
          setNpiLifecycle({});
          const startDate = new ZDate(selectVersion.startDate);
          if (!reduxViewState?.phaseStartDate) {
            setPhaseStartDate(startDate);
          }
          setSelectedFunc("");
        }
      })
      .catch(e => {
        debug(e);
      });
    if (reduxViewState) {
      if (isCustomer) {
        reduxViewState.customerSearch = customerSearch.current;
        reduxViewState.selectedCust = {};
      } else {
        reduxViewState.customerSearch = "";
        reduxViewState.selectedCust = selectedCust;
      }
      if (isItem) {
        reduxViewState.itemSearch = itemSearch.current;
        reduxViewState.selectedItem = {};
      } else {
        reduxViewState.itemSearch = "";
        reduxViewState.selectedItem = selectedItem;
      }
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  const handleSave = () => {
    if (isUndefined(demandResult) || isEmpty(demandResult) || isUndefined(calItemsGridApi)) {
      return;
    }

    const selected = calItemsGridApi.getSelectedRows();
    if (isEmpty(selected)) {
      setSnackInfo({ open: true, severity: inputError, content: t("MSG_NoSelectedData") });
      return;
    }
    setDialogInfo({ open: true, title: "save", content: t("MSG_BfOverideSave") });
  };

  const saveLifecycle = selectedRow => {
    const params = {};
    if (npiLifecycle && Object.keys(npiLifecycle)?.length > 0) {
      params.id = npiLifecycle.id;
      params.itemNodeId = npiLifecycle.itemNodeId;
      params.customerNodeId = npiLifecycle.customerNodeId;
      params.verNum = npiLifecycle.verNum;
    } else {
      params.itemNodeId = selectedRow.itemId;
      params.customerNodeId = selectedRow.customerId;
    }
    params.funcCd = selectedRow.func;
    params.startOfLifeDt = phaseStartDate;
    params.periodVal = phaseRange;

    if (selectedRow.func === functions[4].cd) {
      params.startValRate = singleValue;
      params.endValRate = singleValue;
    } else {
      params.startValRate = sliderValue[0];
      params.endValRate = sliderValue[1];
    }
    npiService.upsertLifecycle(params)
      .then((response) => {
        if (response.data) {
          setNpiLifecycle(response.data);
        }
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        retrieveLifecycle();
      });
  };

  const savePhaseResult = params => {
    setFinishSave(true);
    demandService.phaseByItem(params)
      .catch(e => {
        debug(e);
      })
      .then(() => {
        setFinishSave(false);
      });
  };

  const save = () => {
    const selected = calItemsGridApi.getSelectedRows();
    if (isEmpty(selected)) {
      return;
    }
    const selectedData = selected[0];
    const update = {};
    let i = 0;

    const start = phaseStartDateIdx + 1;
    const end = phaseStartDateIdx + phaseRange;
    const headers = demandResult.headers;
    const newHeader = [];
    for (const header of formatHeaders.current) {
      if (i < start - 1 || i > end - 1) {
        continue;
      }
      update[i] = selectedData[header];
      newHeader.push(headers[i]);
      i++;
    }
    //edit가능한 데이터만 저장
    demandResult.versionDto = selectVersion;
    demandResult.headers = newHeader;
    savePhaseResult({
      ...demandResult,
      edited: update,
      custConfigCd: levelConfig?.objVal?.custLevelCd,
      itemConfigCd: levelConfig?.objVal?.itemLevelCd,
      versionDto: selectVersion
    });
    saveLifecycle(selected[0]);
  };

  const handleDialog = (action, _falseAction) => {
    switch (action) {
      case DIALOG_TITLE.SAVE: {
        save();
        break;
      }
      default:
        break;
    }
  };

  const retrieveCustomer = (custLevel = "CUSTOMER") => {
    customerHrchyService.getByConfig({configCd: levelConfig ? levelConfig?.objVal.custLevelCd : custLevel})
      .then(response => {
        const custHrchy = response.data?.results;
        if (response.data && custHrchy.length > 0) {
          setCustHrchyList(custHrchy);
          reduxViewState.custHrchyList = custHrchy;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveItems = (itemLevel = "ITEM") => {
    itemHrchyService.getByConfig({configCd: levelConfig ? levelConfig?.objVal.itemLevelCd : itemLevel})
      .then(response => {
        const itemHrchy = response.data?.results;
        if (response.data && itemHrchy.length > 0) {
          setItemHrchyList(itemHrchy);
          reduxViewState.itemHrchyList = itemHrchy;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const initialPlanConfig = () => {
    const objVal = { custLevelCd: customer.cd, itemLevelCd: item.cd, itemLevelId: item.id, custLevelId: customer.id }
    const param = {
      moduleCd: DP,
      configCd: BF_LEVEL_CONFIG
    };
    let config = {};
    planConfigService.getConfigCd(param)
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          config = response.data;
          for (const key of Object.keys(config?.objVal)) {
            const value = config.objVal[key];
            if (key === "itemLevelId") {
              config.objVal.itemLevelCd = itemLevelCds?.find(level => level.id === value)?.cd;
            } else if (key === "custLevelId") {
              config.objVal.custLevelCd = custLevelCds?.find(level => level.id === value)?.cd;
            }
          }
        } else {
          config = {configCd: BF_LEVEL_CONFIG, objVal, moduleCd: DP, activeFlg: true};
        }
        setLevelConfig(config)
        //level config 세팅
        const itemCd = config?.objVal?.itemLevelId ? itemLevelCds.find(level => level.id === config.objVal?.itemLevelId) : item;
        const custCd = config?.objVal?.custLevelId ? custLevelCds.find(level => level.id === config.objVal?.custLevelId) : customer;

        setModalItemLevelCd(itemCd)
        setModalCustLevelCd(custCd)
        if (reduxViewState) {
          reduxViewState.itemLevelCd = itemCd;
          reduxViewState.custLevelCd = custCd;
          reduxViewState.levelConfig = config;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
        config = {configCd: BF_LEVEL_CONFIG, objVal, moduleCd: DP, activeFlg: true};
        setModalItemLevelCd(item)
        setModalCustLevelCd(customer)
        setLevelConfig(config)
        if (reduxViewState) {
          reduxViewState.itemLevelCd = item;
          reduxViewState.custLevelCd = customer;
          reduxViewState.levelConfig = config;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })

  };

  const retrieveHrchyConfig = () => {
    if (reduxViewState?.custLevelCds && reduxViewState?.itemLevelCds) {
      const itemCd = reduxViewState ? reduxViewState?.itemLevelCd : item;
      const custCd = reduxViewState ? reduxViewState?.custLevelCd : customer;
      setModalItemLevelCd(itemCd)
      setModalCustLevelCd(custCd)
      return;
    }
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }
            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }
          const itemLevel = [...itemConfig, item];
          const custLevel = [...custConfig, customer]

          setItemLevelCds(itemLevel);
          setCustLevelCds(custLevel);

          if (reduxViewState) {
            reduxViewState.custLevelCds = custLevel
            reduxViewState.itemLevelCds = itemLevel;
            reduxViewState.levelConfig = levelConfig;
            reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const handleSettingClose = () => {
    setSettingModalOpen(false);
  };

  const saveConfig = () => {
    const params = [];
    if (size(levelConfig) > 0) {
      levelConfig.objVal = {
        custLevelId: modalCustLevelCd.id,
        itemLevelId: modalItemLevelCd.id
      }
      params.push({...levelConfig});
    } else {
      params.push({
        moduleCd: "DP",
        configCd: BF_LEVEL_CONFIG,
        activeFlg: true,
        objVal: {
          custLevelId: modalCustLevelCd.id,
          itemLevelId: modalItemLevelCd.id
        }
      });
    }

    planConfigService.upsert(params)
      .then((response) => {
        if (response?.data) {
          const result = response.data[0];
          if (result) {
            result.objVal.custLevelCd = modalCustLevelCd.cd
            result.objVal.itemLevelCd = modalItemLevelCd.cd
          }
          setLevelConfig({...result})
          if (reduxViewState) {
            reduxViewState.levelConfig = {...result};
            reduxViewState.custLevelCd = modalCustLevelCd;
            reduxViewState.itemLevelCd = modalItemLevelCd;
            reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
          }
        }
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        // setReload(true);
        handleSettingClose()
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        handleSettingClose();
      })
    return true;
  };

  const handleItemChange = (event, newValues) => {
    const match = itemHrchyList?.find(item => item.name === newValues);
    if (!isUndefined(match)) {
      setSelectedItem(match)
    }
  };

  const handleCustomerChange = (event, newValues) => {
    const match = custHrchyList?.find(item => item.name === newValues);
    if (!isUndefined(match)) {
      setSelectedCust(match)
    }
  };

  const onGridReadySelectedItems = params => {
    DataState.initialize(params.api);
  };

  const onGridReadyCalItems = params => {
    setCalItemsGridApi(params.api);
    DataState.initialize(params.api);
  };

  const setXS = () => {
    const arr = [];
    const gap = phaseMaxRange - phaseRange;
    if (size(versionHeaders.current) > 0) {
      for (let i = 0; i <= versionHeaders.current.length - gap - 1; i++) {
        arr.push(versionHeaders.current[i]);
      }
      setXs(arr);
    }
  };

  function calculateLog(x) {
    const cal = (sliderValue[1] - sliderValue[0]) / Math.log(phaseRange);
    return cal * Math.log(x) + sliderValue[0];
  }

  function calculateExponential(x) {
    const min = sliderValue[0];
    const max = sliderValue[1];
    return (min / (max / min) ** (min / max)) * ((max / min) ** (1 / (phaseRange - 1))) ** x;
  }

  const interpolate = (spline, targetX) => {
    const result = spline.at(targetX);
    return result.toFixed(2);
  };

  const calculate = (i, startIdx, lastIdx, startValue, _lastValue, quotient) => {
    const index = isEqual(selectAct, actions[0]) ? i - startIdx : lastIdx - i - 1;
    const linear = (startValue + quotient * index).toFixed(2);
    const exponential = calculateExponential(index + 1).toFixed(2);
    const log = index === 0 ? startValue : calculateLog(index + 1).toFixed(2);
    return {
      bucket: xs[i],
      [functions[0].name]: isNaN(linear) ? 0 : linear,
      [functions[1].name]: isNaN(exponential) ? 0 : exponential,
      [functions[2].name]: isNaN(log) ? 0 : log,
      [functions[3].name]: 0,
      [functions[4].name]: isNaN(singleValue) ? 0 : singleValue
    };
  };

  const setYS = () => {
    const chart = [];
    const datas = {};
    if (phaseRange <= 1) {
      return;
    }
    const linearArr = [];
    const exponentialtArr = [];
    const logArr = [];
    const singleArr = [];
    const cubicArr = [];

    const startValue = sliderValue[0];
    const lastValue = sliderValue[1];
    const gap = sliderValue[1] - startValue;
    const quotient = (gap / (phaseRange - 1)).toFixed(2);
    
    for (let i = 0; i < phaseStartDateIdx; i++) {
      const obj = {};
      for (let idx = 0; idx < 4; idx++) {
        obj[functions[idx].name] = 0;
      }
      const bucket = isEqual(selectAct, actions[0]) ? xs[linearArr.length - 1] : xs[linearArr.length];
      const row = {
        bucket,
        ...obj,
      };
      chart.push(row);
      linearArr.push(row[functions[0].name]);
      exponentialtArr.push(row[functions[1].name]);
      logArr.push(row[functions[2].name]);
      singleArr.push(row[functions[4].name]);
    }

    const ysRange = phaseRange < phaseMaxRange ? phaseRange + phaseStartDateIdx - 1 : versionDateList?.length - 1;
    const startIdx = isEqual(selectAct, actions[0]) ? phaseStartDateIdx : phaseStartDateIdx + 1;
    const lastIdx = isEqual(selectAct, actions[0]) ? ysRange : ysRange + 1;
    for (let i = startIdx; i < lastIdx; i++) {
      const row = calculate(i, startIdx, lastIdx, startValue, lastValue, quotient);
      chart.push(row);
      linearArr.push(row[functions[0].name]);
      exponentialtArr.push(row[functions[1].name]);
      logArr.push(row[functions[2].name]);
      singleArr.push(row[functions[4].name]);
    }
    
    if (isEqual(selectAct, actions[0])) {
      linearArr.push(lastValue.toFixed(2));
      exponentialtArr.push(lastValue.toFixed(2));
      logArr.push(lastValue.toFixed(2));
      singleArr.push(singleValue);
    } else {
      linearArr.unshift(lastValue.toFixed(2));
      exponentialtArr.unshift(lastValue.toFixed(2));
      logArr.unshift(lastValue.toFixed(2));
      singleArr.unshift(singleValue);
    }
    
    const obj = {};
    for (let i = 0; i < 4; i++) {
      obj[functions[i].name] = lastValue;
    }
    if (isEqual(selectAct, actions[0])) {
      chart.push({
        bucket: xs[linearArr.length - 1],
        ...obj,
        [functions[4].name]: singleValue,
      });
    } else {
      chart.splice(phaseStartDateIdx, 0, {
        bucket: xs[phaseStartDateIdx],
        ...obj,
        [functions[4].name]: singleValue,
      });
    }
    datas[functions[0].name] = linearArr;
    datas[functions[1].name] = exponentialtArr;
    datas[functions[2].name] = logArr;
    datas[functions[4].name] = singleArr;
    
    const cubicXs = Array.from({ length: xs.length }, (_v , i)=> i + 1);
    if (size(cubicXs) > 0) {
      const spline = new Spline(cubicXs, linearArr);
      const num = versionDateList?.length;
      for (let i = 0; i < num; i++) {
        const x = cubicXs[0] + (i / (num - 1)) * (cubicXs[cubicXs.length - 1] - cubicXs[0]);
        const y = interpolate(spline, x) + startValue;
        const data = chart[i];
        if (data && i >= phaseStartDateIdx) {
          data[functions[3].name] = y;
        }
        cubicArr.push(y);
      }
      datas[functions[3].name] = cubicArr;
    }
    
    setYs(datas);
    setChartData(chart);
  };

  const renderBackground = (isFunChart) => {
    const start = isFunChart ? xs[phaseStartDateIdx] : formatHeaders.current[phaseStartDateIdx];
    const end = isFunChart ? xs[phaseStartDateIdx + phaseRange - 1] : formatHeaders.current[phaseStartDateIdx + phaseRange - 1];
    
    return (
      <ReferenceArea
        x1={start}
        x2={end}
        y1={0}
        y2={100}
        fill={colorMap.phaseBackground}
        fillOpacity={0.3}
        type="monotone"
      />
    );
  };

  const onFirstDataRendered = () => {
    if (calItemsGridApi) {
      calItemsGridApi.forEachNode(node => {
        if (selectedFunc) {
          node.setSelected(node.data && node.data.func === selectedFunc);
        }
      });
      GridUtils.focusOnSelectedRow(calItemsGridApi);
    }
  };

  const setToolTip = version => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE: startDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = version?.planConfigObj;
      let format = "yyyy-MM";
      if (codeUtil.isWeekBucket(unit)) {
        format = "yyyy-MM-dd";
      }
      return `${dateUtil.formatDate(startDate, format)} ~
      ${dateUtil.formatDate(dateUtil.calculateRangeDate(startDate, unit, startDay, range, true), format)}`;
    }
    return null;
  };

  const cloneAddRow = () => {
    GridUtils.focusOnSelectedRow(calItemsGridApi);
    const selectedRow = calItemsGridApi.getSelectedRows()[0];
    let isSelected = false;

    if (size(selectedRow) < 1) {
      setSnackInfo({ open: true, severity: inputError, content: t("MSG_NoSelectedRow") });
    } else {
      isSelected = true;
    }

    if (isSelected) {
      const newCloneRow = {
        ...selectedRow,
        func: "userCustom",
        isNew: true
      }
      DataState.setDataState(GridUtils.addRow(calItemsGridApi, newCloneRow));
    }
  };

  const removeRow = () => {
    const selectRow = calItemsGridApi.getSelectedRows();
    const userSelectRow = selectRow.filter(row => row.isNew);
    calItemsGridApi.applyTransaction({ remove: userSelectRow });
  };

  const onCellValueChanged = e => {
    if (e?.data?.isNew) {
      const newRowData = setCustomChart(valueChartData, e.data);
      setValueChartData([...newRowData]);
    }
  };

  const initPageToRetrieve = () => {
    if ((!isEmpty(itemSearch.current) || !isEmpty(selectedItem)) && (!isEmpty(customerSearch.current) || !isEmpty(selectedCust)) && size(selectVersion) > 0) {
      retrieveLifecycle();
    }
  };

  const handleReset = () => {
    setNpiLifecycle(null);
    itemSearch.current = "";
    customerSearch.current = "";
    setSelectedItem({});
    setSelectedCust({});
    setGridData([]);
    setRenderedGridData([])
  };

  useEffect(() => {
    if (!isEmpty(levelConfig)) {
      if (reduxViewState?.levelConfig !== levelConfig) {
        handleReset();
        retrieveCustomer();
        retrieveItems();
      } else {
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelConfig]);

  useEffect(() => {
    if (!isEmpty(customerSearch.current)) {
      setNpiLifecycle(null);
      setRenderedGridData([]);
      setGridData([]);
      // setSelectedItem({});
      // setSelectedCust({});
      // customerSearch.current = "";
      // retrieveItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearch.current]);

  useEffect(() => {
    if (selectedCust !== null && Object.keys(selectedCust).length > 0) {
      setNpiLifecycle(null);
      setGridData([]);
      setRenderedGridData([])
      itemSearch.current = "";
      // retrieveItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCust]);


  useEffect(() => {
    if ((!isEmpty(itemSearch.current) || !isEmpty(selectedItem)) && (!isEmpty(customerSearch.current) || !isEmpty(selectedCust)) && size(selectVersion) > 0) {
      retrieveLifecycle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, selectedCust, selectVersion]);

  useEffect(() => {
    if (npiLifecycle && size(selectVersion) > 0) {
      retrieveDemandByItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [npiLifecycle, selectVersion]);

  useEffect(() => {
    if (selectFunc.length > 0 && !loading.current) {
      setCalGridData(calculatedPhaseState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectFunc, ys]);

  useEffect(() => {
    if (selectFunc.length > 0) {
      setCalGridData(calculatedPhaseState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData]);

  useEffect(() => {
    if (size(calGridData) > 0) {
      setRenderedGridData(calGridData);
      loading.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calGridData]);

  useEffect(() => {
    if (size(renderedGridData) > 0) {
      onFirstDataRendered();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedGridData]);

  useEffect(() => {
    if (phaseRange > 1) {
      setYS();
      loading.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xs, sliderValue, phaseRange]);
  
  useEffect(() => {
    if (phaseRange > 1) {
      if (versionHeaders.current.length > 1) {
        setCalGridHeaders(versionHeaders.current, bucketCode.WEEK);
        setXS();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseRange]);

  useEffect(() => {
    if (!isUndefined(selectVersion?.startDate)) {
      let nearDate = new ZDate(selectVersion?.startDate);
      if (codeUtil.isWeekBucket(selectVersion?.bucket)) {
        nearDate = dateUtil.nearDay(new ZDate(phaseStartDate), selectVersion?.startDay, selectVersion?.bucket);
      } else {
        nearDate = dateUtil.startOfMonthDate(new ZDate(phaseStartDate), selectVersion?.bucket);
      }
      let gap = 0;
      const index = versionDateList?.indexOf(dateUtil.headerDateFormatter(nearDate, selectVersion?.bucket));
      gap = versionDateList?.length - index;
      if (phaseMaxRange !== gap) {
        setPhaseMaxRange(gap);
      }
      if (phaseRange !== gap) {
        setPhaseRange(gap);
      }

      const idxGap = versionDateList?.length - gap;
      setPhaseStartDateIdx(idxGap);
      if (reduxViewState) {
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, phaseStartDateIdx: idxGap }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseStartDate]);

  useEffect(() => {
    setYS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleValue]);

  useEffect(() => {
    if (selectAct) {
      setYS();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAct]);

  useEffect(() => {
    if (!isUndefined(selectVersion) && !isEmpty(selectVersion)) {
      const startDate = new ZDate(selectVersion.startDate);
      if (!reduxViewState?.phaseStartDate) {
        setPhaseStartDate(startDate);
      }
      setPhaseMinDate(startDate);
      setPhaseMaxDate(dateUtil.calculateRangeDate(startDate, selectVersion.bucket, selectVersion.startDay, selectVersion.planningRange, true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion]);

  useEffect(() => {
    if (custLevelCds != null && !isEmpty(custLevelCds)) {
      initialPlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custLevelCds])

  useEffect(() => {
    if (isEmpty(selectVersion)) {
      retrievePlanversion();
      retrieveHrchyConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }}
        open={finishSave}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <FilterContainer>
        <Tooltip
          title={setToolTip(selectVersion)}
          placement="bottom-start">
          <Box sx={{ color: "#000!important" }}>
            <FormControl sx={{ width: 200, mr: 1 }}>
              <TextField
                label={t("version")}
                size="small"
                value={selectVersion?.name ?? ""}
                name="version"
                type="string"
                sx={{ textAlign: "center", cursor: "pointer" }}
                InputProps={{ readOnly: true }}
              />
            </FormControl>
          </Box>
        </Tooltip>
        <FormControl sx={{ width: 200, minWidth: 100, mr: 1 }}>
          <InputLabel id="action-label">{t("actions")}</InputLabel>
          <Select
            labelId="action-label"
            id="npi-action"
            size="small"
            name={t("actions")}
            value={selectAct}
            onChange={handleChange}
            input={<OutlinedInput label={t("actions")} />}>
            {actions?.map(act => (
              <MenuItem
                key={act}
                value={act}>
                {t(act)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        { isCustomer ?
          <CustomerAutocomplete
            setSelectedCustomer={initPageToRetrieve}
            searchValue={customerSearch}
            searchFields={ElasticSearchUtil.defaultCustomerSearchFields}
            sx={{ display: "inline-block", width: 240, mr: 1, }}
          /> :
          <FormControl size="small" sx={{ width: 240, minWidth: 100, mr: 1 }}>
            {/*auto complete 추가 */}
            <Autocomplete
              value={selectedCust?.name ?? ""}
              onChange={handleCustomerChange}
              inputValue={inputCustValue}
              onInputChange={(event, newInputValue) => {
                setInputCustValue(newInputValue);
              }}
              id="controllable-states-demo"
              options={custHrchyList?.map(el => el.name)}
              size="small"
              renderInput={(params) => <TextField {...params} label={t("customerHrchy")} />}
            />
          </FormControl>
        }
        { isItem ?
          <DPItemAutocomplete
            setSelectedItem={initPageToRetrieve}
            searchValue={itemSearch}
            searchFields={ElasticSearchUtil.defaultItemSearchFields}
            retrieve={retrieveCustomer}
            sx={{ display: "inline-block", width: 240 }}
          /> :
          <FormControl size="small" sx={{ width: 240, minWidth: 100, mr: 1 }}>
            {/*auto complete 추가 */}
            <Autocomplete
              value={selectedItem?.name ?? ""}
              onChange={handleItemChange}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              id="controllable-states-demo"
              options={itemHrchyList?.map(el => el.name)}
              size="small"
              renderInput={(params) => <TextField {...params} label={t("itemHrchy")} />}
            />
          </FormControl>
        }
        <SettingsButton onClick={() => setSettingModalOpen(true)} sx={{ ml: "auto" }}/>
      </FilterContainer>
      <Box className={"grid-area"}>
        <Box
          className="ag-theme-balham"
          sx={{ width: "100%", height: "100px", minWidth: 1100 }}>
          <AgGridReact
            ref={selectedItemsGridRef}
            onGridReady={onGridReadySelectedItems}
            rowData={gridData}
            columnDefs={GridUtils.getColumnDefs(gridHeader)}
            {...DefaultGridSetting({ title })}
          />
        </Box>
        <Box sx={{ height: "45px", display: "flex", mt: 2, ml: 0, gap: 1, minWidth: 1100 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("applyPeriodStart")}
              value={phaseStartDate}
              maxDate={phaseMaxDate}
              minDate={phaseMinDate}
              format={codeUtil.isWeekBucket(selectVersion?.bucket) ? "yyyy-MM-dd" : "yyyy-MM"}
              views={codeUtil.isWeekBucket(selectVersion?.bucket) ? ["year", "month", "day"] : ["year", "month"]}
              shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(new ZDate(e), selectVersion?.bucket, selectVersion?.startDay)}
              openTo={"month"}
              onAccept={newStartValue => {
                loading.current = false;
                const start = codeUtil.isWeekBucket(selectVersion?.bucket) ? newStartValue : dateUtil.startOfMonthDate(newStartValue);
                setPhaseStartDate(start);
                reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, phaseStartDate: start }));
              }}
              slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
            />
          </LocalizationProvider>
          <FormControl size="small">
            <TextField
              label={t("bucket")}
              size="small"
              value={phaseRange}
              name="planRange"
              type="number"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0, max: phaseMaxRange }}
              sx={{ textAlign: "center", width: 140 }}
              onChange={event => {
                const newValue = parseInt(event.target.value, 10);
                if (newValue > 0 && newValue <= phaseMaxRange) {
                  loading.current = false;
                  setPhaseRange(newValue);
                  reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, phaseRange: newValue }));
                }
              }}
            />
          </FormControl>
          <FormControl size="small">
            <Autocomplete
              multiple
              options={functions}
              name={t("function")}
              getOptionLabel={option => option.name}
              renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                  const colorIdx = functions.indexOf(functions.filter(fun => isEqual(fun.name, option.name))[0]);
                  return(
                    <Chip
                      key={option.cd}
                      label={option.name}
                      size="small"
                      {...getTagProps({index})}
                      sx={funcChipSx(functionColors[colorIdx])}
                    />
                  )}
                )
              }
              value={selectFunc}
              filterSelectedOptions
              size="small"
              sx={{ minWidth: 150 }}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              onChange={
                (_, newValue) => {
                  handleFuncChange(newValue)
                  reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, selectFunc: newValue }));
                } 
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label={t("function")}
                />
              )}
            />
          </FormControl>
          <FormControl size="small">
            <TextField
              label={t("singleVal")}
              size="small"
              value={singleValue}
              name="singleVal"
              type="number"
              disabled={!selectFunc.map(el => el.name).includes(functions[4].name)}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0, max: sliderValue[1] }}
              sx={{ textAlign: "center", width: 140 }}
              onChange={event => {
                const newValue = parseInt(event.target.value, 10);
                if (newValue > 0 && newValue <= sliderValue[1]) {
                  loading.current = false;
                  setSingleValue(newValue);
                  reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, singleValue: newValue }));
                }
              }}
            />
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", mt: 2, height: "calc(50% - 75px)", minWidth: 1100, maxWidth: "calc(100% - 50px)", minHeight: 250 }}>
          <Box
            sx={{ width: "2%", height: "100%", ml: 5, pt: "6px", pb: "55px", boxSizing: "border-box" }}>
            {/* Target Item Selection 의 범위 슬라이더와 같은 규격 — 값 항상 표시 + 핸들 교차 방지 */}
            <Slider
              getAriaLabel={() => "Function range"}
              value={sliderValue}
              onChange={handleSliderChange}
              onChangeCommitted={(_) => setRenderedGridData(calGridData)}
              valueLabelDisplay="on"
              getAriaValueText={valuetext}
              disableSwap
              marks
              orientation="vertical"
            />
          </Box>
          <ResponsiveContainer
            className={"dp-lifecycle-mgmt"}
            width="30%"
            height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" />
              <YAxis
                label={{ value: "(%)", position: "insideRight" }}
                orientation="right"
                domain={[0, 100]}
              />
              <RechartTooltip content={<AgStyleTooltipContent />} />
              <Legend />
              {renderBackground(true)}
              {selectFunc.map((el) => (
                <Line
                  key={el.name}
                  type="monotone"
                  dataKey={el.name}
                  stroke={
                    selectedFunc === "" || el.cd === selectedFunc
                      ? el.color : unSelectedColor
                  }
                  strokeWidth={selectedFunc !== "" && el.cd === selectedFunc ? 4 : 2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <Box sx={{ width: "70%", height: "100%" }}>
            <ChartComposed
              xAxis={'bucket'}
              dataSeries={chartSeries}
              data={valueChartData}
              options={{ margin: { left: 10, right: 30, bottom: 0, top: 0}, minWidth: 0, minHeight: 0 }} //  xAxisInterval: null,
              strokeByField={strokeByField}
            />
          </Box>
        </Box>
        <Box sx={{ width: "100%", height: "calc(50% - 105px)", minHeight: 200, minWidth: 1100 }}>
          <Box sx={{ height: "40px", display: "flex", ml: 1, mt: 0, justifyContent: "flex-end" }}>
            <RemoveButton onClick={removeRow} />
            <AddButton onClick={cloneAddRow} />
            <SaveButton label="confirm" onClick={handleSave} disabled={selectVersion?.statusCd === "RELEASED"}/>
          </Box>
          <Box
            id ="lifecycle-mgmt-grid"
            className="ag-theme-balham"
            sx={{ height: "calc(100% - 40px)" }}>
            <AgGridReact
              ref={calItemsGridRef}
              onGridReady={onGridReadyCalItems}
              rowData={renderedGridData}
              columnDefs={GridUtils.getColumnDefs(calGridHeader)}
              {...DefaultGridSetting({ title })}
              rowSelection={{ mode: 'singleRow', enableClickSelection: false, checkboxes: false, headerCheckbox: false }}
              onSelectionChanged={onSelectionChanged}
              onCellValueChanged={onCellValueChanged}
            />
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <BaseConfigModal
        open={settingModalOpen}
        onClose={handleSettingClose}
        title={t("levelSetting")}
        onSave={() => saveConfig()}
        size="lg">
        {/* handleChange 는 event.target.name 으로 축을 가른다 — 세그먼트는 값만 주므로 같은 모양으로 만들어 넘긴다 */}
        <HrchyLevelSegments
          label={t(CUST_LEVEL)}
          levels={custLevelCds}
          value={modalCustLevelCd?.id}
          onChange={id => handleChange({ target: { name: CUST_LEVEL, value: id } })}
        />
        <HrchyLevelSegments
          label={t(ITEM_LEVEL)}
          levels={itemLevelCds}
          value={modalItemLevelCd?.id}
          onChange={id => handleChange({ target: { name: ITEM_LEVEL, value: id } })}
          sx={{ mt: 2 }}
        />
      </BaseConfigModal>
    </>
  );
};

export default withTranslation()(LifecycleMgmt);
